import { useMemo } from "react";

import {
  RequestableSaasDestination,
  SaaSDestination,
} from "../destinations/destinations";
import useAllSaasDestinationsByType from "../hooks/useAllSaasDestinationsByType";
import { LoadedDestinationInfo } from "../types/global";

/* The minimum destination data needed to lookup a label */
type DisplayableDestination = Pick<
  LoadedDestinationInfo,
  "custom_label" | "instance_name" | "name"
>;

export function lookUpDestinationLabel(
  allDestinations: Record<string, SaaSDestination | RequestableSaasDestination>,
  destinationConfig?: DisplayableDestination,
) {
  if (destinationConfig === undefined) {
    return "";
  }

  if (destinationConfig.custom_label !== "") {
    return destinationConfig.custom_label;
  }

  const saasDestination = allDestinations[destinationConfig.name];

  return getDestinationLabel(destinationConfig, saasDestination);
}

export function getDestinationLabel(
  destinationConfig: DisplayableDestination,
  saasDestination?: SaaSDestination | RequestableSaasDestination,
) {
  if (destinationConfig.custom_label) {
    return destinationConfig.custom_label;
  }

  if (
    saasDestination === undefined ||
    !("supportsMultiConfig" in saasDestination) ||
    destinationConfig.instance_name.trim() === ""
  ) {
    return destinationConfig.instance_name.trim() === ""
      ? ""
      : destinationConfig.instance_name;
  }

  return `${saasDestination.multiConfigLabel}: ${destinationConfig.instance_name}`;
}

export default function useDestinationLabel(
  destination?: LoadedDestinationInfo,
) {
  const allDestinations = useAllSaasDestinationsByType();

  return useMemo(() => {
    return lookUpDestinationLabel(allDestinations, destination);
  }, [destination, allDestinations]);
}
